import React, { useContext } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '@airbus/components-react/components/Modal'
import FormInput from 'components/FormFields/FormInput'
import { Button, Table, TableHead, TableBody, TableRow, Inline, Container } from '@airbus/components-react/components'
import GoToTop from 'components/goToTop'
import { useForm, FormProvider } from 'react-hook-form'
import URL_APIS from 'utils/api/urlApis'
import PageHeader from 'components/pageHeader'
import { Lang } from 'context/LangContext'
import { CriteriaStaff, UserQualification } from 'types/workorder/manHour'
import useQuery from 'utils/hooks/useQuery'
import LightDarkContext from 'context/LightDarkModeContext'
import ResponsiveTableCell from 'components/responsiveTableCell'

const ID_SCROLL_COMPONENT_FOR_GOTOTOP = 'modalStaffGoToTop'

type Props = {
  lang: Lang
  isOpened: boolean
  onCloseModal: () => void
  onStaffSelected: (uq: UserQualification) => void
}

const styleModal = {
  position: 'absolute',
  transform: 'translate(50%, 50%)',
  width: '50%',
  height: '50%',
  border: '2px solid #000',
  boxShadow: 24,
  overflowY: 'scroll',
  scrollbarWidth: 'none',
}

const ModalStaff = ({ lang, isOpened, onCloseModal, onStaffSelected: onStaffSelected }: Props) => {
  const { t } = useTranslation()

  const [data, setData] = useState<UserQualification[]>()
  const [isLoading, setIsLoading] = useState(false)

  const { fetchData: fetchStaff } = useQuery<UserQualification[]>(URL_APIS.URL_WORKORDER_PERSONNEL_SEARCH_GET)

  const methods = useForm<CriteriaStaff>({
    defaultValues: { staffContains: '', qualificationContains: '' },
  })

  const { handleSubmit, resetField } = methods

  const handleSearchSubmit = async (data: CriteriaStaff) => {
    setIsLoading(true)

    const staffResponse = await fetchStaff({
      lang: lang,
      code: data.staffContains.toUpperCase(),
      qualif: data.qualificationContains.toUpperCase(),
    })
    setIsLoading(false)

    setData(staffResponse?.data?.content)
  }

  const clearForm = () => {
    resetField('staffContains')
    resetField('qualificationContains')
    setData([])
  }

  const handleCloseModal = () => {
    clearForm()
    onCloseModal()
  }

  const handleStaffSelection = (staffSelected: UserQualification) => {
    clearForm()
    onStaffSelected(staffSelected)
  }
  const { getBackgroundColor } = useContext(LightDarkContext)

  return (
    <Modal
      open={isOpened}
      onClose={handleCloseModal}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <div style={{ ...styleModal, backgroundColor: getBackgroundColor() }} id={ID_SCROLL_COMPONENT_FOR_GOTOTOP}>
        <>
          <PageHeader
            pageTitle={t('modal_search_staff')}
            modalEmbedded={true}
            modalOnClose={onCloseModal}
            style={{ top: 0 }}
            localLoading={isLoading}
          />

          <Container style={{ flex: 1 }} fixed>
            <div style={{ padding: '1em' }}>
              <FormProvider {...methods}>
                <form style={{ marginTop: '1em' }} onSubmit={handleSubmit(handleSearchSubmit)}>
                  <Inline spacing='2-x' style={{ width: '100%' }}>
                    <FormInput
                      name='staffContains'
                      rules={{ required: false }}
                      label={t('9100287')}
                      maxLength={16}
                      capitalize
                    />
                    <FormInput
                      name='qualificationContains'
                      rules={{ required: false }}
                      label={t('7000914')}
                      maxLength={16}
                      capitalize
                    />
                  </Inline>
                  <div style={{ width: '100%' }}>
                    <Inline spacing='1-x' style={{ margin: '10px 0' }}>
                      <Button variant='secondary' onClick={handleCloseModal}>
                        {t('8000569')}
                      </Button>
                      <Button
                        className={methods.formState.isDirty ? 'btn_color_primary' : ''}
                        variant='primary'
                        type='submit'
                        disabled={!methods.formState.isDirty || isLoading}
                      >
                        {t('7001655')}
                      </Button>
                    </Inline>
                  </div>
                </form>
              </FormProvider>
              {data && data.length > 0 ? (
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <ResponsiveTableCell>{t('0MRM019')}</ResponsiveTableCell>
                      <ResponsiveTableCell>{t('7000914')}</ResponsiveTableCell>
                      <ResponsiveTableCell>{t('1CEF000')}</ResponsiveTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.map((row: UserQualification, i: number) => (
                      <TableRow key={'staffQualif_' + i} hover={true} onClick={() => handleStaffSelection(row)}>
                        <ResponsiveTableCell>{row?.code}</ResponsiveTableCell>
                        <ResponsiveTableCell>{row?.qualification}</ResponsiveTableCell>
                        <ResponsiveTableCell>{row?.applicability}</ResponsiveTableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div>{t('9500055')}</div>
              )}
            </div>
          </Container>
          <GoToTop idComponentForScroll={ID_SCROLL_COMPONENT_FOR_GOTOTOP} />
        </>
      </div>
    </Modal>
  )
}

export default ModalStaff
